import type { ComponentType, FunctionComponent, ReactNode } from 'react';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

import { Link, time } from '@dr.pogodin/react-utils';

import { useExperiences } from 'hooks';
import type {
  GetAggregatedUserScoresResT,
  GetHistoricalUserScoresResT,
} from 'server/controllers/user-scores';
import {
  type ExperienceT,
  LIFE_MOMENTS,
  type SessionT,
  type UserScoreKindT,
  type UserScoreT,
} from 'utils';

import IconArrowRight from 'assets/images/ai/arrow-right.svg';
import IconEnergy from 'assets/images/ai/icon-energy.svg';
import IconIntentionally from 'assets/images/ai/icon-intentionally.svg';
import IconPresence from 'assets/images/ai/icon-presence.svg';
import IconSummaryCompleted from 'assets/images/ai/icon-summary-completed.svg';
import IconSummaryCompletion
  from 'assets/images/ai/icon-summary-completion.svg';
import IconSummaryExperience
  from 'assets/images/ai/icon-summary-experience.svg';
import IconSummaryTransformation
  from 'assets/images/ai/icon-summary-transformation.svg';
import IconTransformationDown
  from 'assets/images/ai/icon-transformation-down.svg';
import IconTransformationUp from 'assets/images/ai/icon-transformation-up.svg';

import './style.scss';

type PropsT = {
  aggregated: GetAggregatedUserScoresResT | null;
  children?: ReactNode;
  historical: GetHistoricalUserScoresResT | null;
  loading?: boolean;
  scores: Partial<Record<UserScoreKindT, UserScoreT>>;
  sessions: SessionT[];
  subtitle: string;
  title: string;
};

const MAX_HISTORY_ITEMS = 7;

type ChartTooltipPropsT = {
  active?: boolean;
  color?: string;
  dataKey?: 'ENERGY' | 'INTENTION' | 'PRESENCE';
  payload?: Array<{ payload: GetHistoricalUserScoresResT[number] }>;
};

const ChartTooltip: FunctionComponent<ChartTooltipPropsT> = ({
  active,
  color,
  dataKey,
  payload,
}) => {
  const point = payload?.[0]?.payload;
  if (!active || !dataKey || !point) return null;

  const value = point[dataKey];

  return (
    <div styleName="chartTooltip">
      <div styleName="chartTooltipDate">{time(point.day).format('MMM D')}</div>
      <div style={{ color }} styleName="chartTooltipValue">
        {typeof value === 'number' ? value : 0}
      </div>
    </div>
  );
};

const DAY_SCORE_KINDS = ['PRESENCE', 'ENERGY', 'INTENTION'] as const;

// Used as a fallback whenever real data is missing for a given experience
// history row (e.g. the description, which has no equivalent field in the
// data model, or the day's dominant score, when no score records exist yet
// for that day).
const HISTORY_FALLBACKS = [
  { description: 'Find inner peace and clarity', scoreLabel: '+2', scoreWord: 'presence' },
  { description: 'Boost your focus and productivity', scoreLabel: '+3', scoreWord: 'energy' },
  { description: 'Connect with your loved ones', scoreLabel: '+4', scoreWord: 'intention' },
  { description: 'Prepare for a restful sleep', scoreLabel: '+2', scoreWord: 'presence' },
  { description: 'Focus and alignment', scoreLabel: '+5', scoreWord: 'energy' },
];

type HistoryItemT = {
  description: string;
  finishedAt: number;
  icon: ComponentType;
  key: string;
  scoreLabel: string;
  scoreWord: string;
  title: string;
};

function getDominantDayScore(
  day: GetHistoricalUserScoresResT[number] | undefined,
  previousDay: GetHistoricalUserScoresResT[number] | undefined,
): { scoreLabel: string; scoreWord: string } | undefined {
  let best: { kind: typeof DAY_SCORE_KINDS[number]; value: number } | undefined;

  for (const kind of DAY_SCORE_KINDS) {
    const value = day?.[kind];
    if (typeof value === 'number' && value > 0 && (!best || value > best.value)) {
      best = { kind, value };
    }
  }

  if (!best) return undefined;

  const previousValue = previousDay?.[best.kind];
  const diff = best.value - (typeof previousValue === 'number' ? previousValue : 0);

  return {
    scoreLabel: `${diff >= 0 ? '+' : '-'}${Math.abs(diff)}`,
    scoreWord: best.kind.toLowerCase(),
  };
}

function buildHistoryItems(
  historical: GetHistoricalUserScoresResT | null,
  sessions: SessionT[],
  experiences: Record<string, { data: ExperienceT | null }>,
): HistoryItemT[] {
  if (!historical?.length) return [];

  const sessionsByDay = new Map<string, SessionT[]>();
  for (const session of sessions.filter((s) => s.finishedAt)) {
    const day = time(session.finishedAt).format('YYYY-MM-DD');
    const daySessions = sessionsByDay.get(day) ?? [];
    daySessions.push(session);
    sessionsByDay.set(day, daySessions);
  }

  const latestDays = historical.slice(-MAX_HISTORY_ITEMS);
  const startIndex = historical.length - latestDays.length;

  return latestDays
    // eslint-disable-next-line complexity
    .map((day, index) => {
      const previousDay = historical[startIndex + index - 1];
      const daySessions = sessionsByDay.get(day.day) ?? [];
      const [session] = daySessions
        .sort((a, b) => (b.finishedAt ?? 0) - (a.finishedAt ?? 0));

      const finishedAt = session?.finishedAt
        ?? time(day.day).valueOf();
      const experience = session
        ? experiences[session.experienceId]?.data
        : undefined;
      const lifeMoment = experience?.lifeMomentId
        ? LIFE_MOMENTS[experience.lifeMomentId]
        : undefined;

      const fallback = HISTORY_FALLBACKS[index % HISTORY_FALLBACKS.length];
      if (!fallback) throw Error('Missing fallback');

      const dayScore = getDominantDayScore(day, previousDay);

      return {
        description: fallback.description,
        finishedAt,
        icon: lifeMoment?.icon
          ?? LIFE_MOMENTS['99_UNIQUE_UNKNOWN_LIFE_MOMENT'].icon,
        key: session?.id ?? `${day.day}-${index}`,
        scoreLabel: dayScore?.scoreLabel ?? fallback.scoreLabel,
        scoreWord: dayScore?.scoreWord ?? fallback.scoreWord,
        title: lifeMoment?.name ?? experience?.title ?? 'Experience',
      };
    })
    .reverse();
}

function getNumericScoreValue(score: UserScoreT | undefined): number {
  return typeof score?.value === 'number' ? score.value : 0;
}

function getDayValue(
  day: GetHistoricalUserScoresResT[number] | undefined,
  kind: 'ENERGY' | 'INTENTION' | 'PRESENCE' | 'TRANSFORMATION',
): number {
  const value = day?.[kind];
  return typeof value === 'number' ? value : 0;
}

function getLatestDayValue(
  historical: GetHistoricalUserScoresResT | null,
  kind: 'ENERGY' | 'INTENTION' | 'PRESENCE' | 'TRANSFORMATION',
  fallback: number,
): number {
  if (!historical?.length) return fallback;
  return getDayValue(historical[historical.length - 1], kind);
}

function getTransformationDisplayMetrics(
  historical: GetHistoricalUserScoresResT | null,
  fallbackScore: number,
  fallbackTrend: number,
): {
  displayTransformationScore: number;
  displayTrend: number;
} {
  if (!historical?.length) {
    return {
      displayTransformationScore: fallbackScore,
      displayTrend: fallbackTrend,
    };
  }

  const [oldestDay] = historical;
  const latestDay = historical[historical.length - 1];
  const latestTransformationValue = getDayValue(latestDay, 'TRANSFORMATION');
  const oldestTransformationValue = getDayValue(oldestDay, 'TRANSFORMATION');

  return {
    displayTransformationScore: latestTransformationValue,
    displayTrend: latestTransformationValue - oldestTransformationValue,
  };
}

const LeftCol: FunctionComponent<PropsT> = ({
  aggregated,
  children,
  historical,
  scores,
  sessions,
  subtitle,
  title,
}) => {
  const transformationScore = aggregated?.totalScore ?? 0;
  const scoreTrend = aggregated?.trendScore ?? 0;

  const presenceScore = getLatestDayValue(
    historical,
    'PRESENCE',
    aggregated?.presence30Days ?? 0,
  );
  const energyScore = getLatestDayValue(
    historical,
    'ENERGY',
    aggregated?.energy30Days ?? 0,
  );
  const intentionScore = getLatestDayValue(
    historical,
    'INTENTION',
    aggregated?.intention30Days ?? 0,
  );

  const {
    displayTransformationScore,
    displayTrend,
  } = getTransformationDisplayMetrics(
    historical,
    transformationScore,
    scoreTrend,
  );

  const recentExperienceIds = sessions.map((s) => s.experienceId);
  const { items: experienceItems } = useExperiences(recentExperienceIds);

  const historyItems = buildHistoryItems(
    historical,
    sessions,
    experienceItems,
  );

  const experiencesCount = getNumericScoreValue(
    scores.PRACTICE_STARTED_COUNT_LIFETIME,
  );
  const completedCount = getNumericScoreValue(
    scores.PRACTICE_COMPLETION_COUNT_30D,
  );
  const completionRate = getNumericScoreValue(
    scores.PRACTICE_COMPLETION_PERCENTAGE_30D,
  );

  const growthData = [
    {
      icon: IconSummaryExperience,
      subtitle: 'All Time',
      title: 'Experiences',
      value: experiencesCount.toString(),
    },
    {
      icon: IconSummaryCompleted,
      subtitle: 'Last 30 days',
      title: 'Completed',
      value: completedCount.toString(),
    },
    {
      icon: IconSummaryCompletion,
      subtitle: 'Last 30 days',
      title: 'Completion Rate',
      value: `${Math.round(completionRate)}%`,
    },
    {
      icon: IconSummaryTransformation,
      subtitle: 'Last 30 days',
      title: 'Transformation Points',
      value: `${displayTrend >= 0 ? '+' : '-'}${Math.abs(displayTrend)}`,
    },
  ];

  return (
    <div styleName="leftCol">
      <div styleName="header">
        <h1>{title}</h1>
        <h2>{subtitle}</h2>
      </div>

      <div styleName="transformationScore">
        <div styleName="scoreInfo">
          <div styleName="scoreTitle">Your Transformation Score</div>
          <div styleName="scoreValue">
            <span styleName="current">{displayTransformationScore}</span>
            <span styleName="total">/100</span>
          </div>
          <div styleName="scoreTrend">
            {displayTrend >= 0
              ? <IconTransformationUp />
              : <IconTransformationDown />}
            <span>
              {' '}
              <b>{Math.abs(displayTrend)}</b>
              {' '}
              points in the last 30 days
            </span>
          </div>
        </div>
      </div>

      <div styleName="mainContent">
        <div styleName="statsGrid">
          <div styleName="statCol">
            <div styleName="statHeader">
              <IconPresence styleName="statIcon" />
              <div styleName="statTitle">Presence</div>
            </div>
            <div styleName="statScore">
              <span styleName="current">{presenceScore}</span>
              <span styleName="total">/100</span>
            </div>
            <div styleName="statSubtitle">Presence</div>
            <div styleName="statChart">
              <ResponsiveContainer height="100%" width="100%">
                <AreaChart data={historical ?? []}>
                  <defs>
                    <linearGradient
                      id="colorPresence"
                      x1="0"
                      x2="0"
                      y1="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#d63384" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#d63384" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Tooltip
                    content={<ChartTooltip color="#d63384" dataKey="PRESENCE" />}
                  />
                  <Area
                    dataKey="PRESENCE"
                    fill="url(#colorPresence)"
                    fillOpacity={1}
                    stroke="#d63384"
                    strokeWidth={2}
                    type="monotone"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div styleName="statCol">
            <div styleName="statHeader">
              <IconEnergy styleName="statIcon" />
              <div styleName="statTitle">Energy</div>
            </div>
            <div styleName="statScore">
              <span styleName="current">{energyScore}</span>
              <span styleName="total">/100</span>
            </div>
            <div styleName="statSubtitle">Energy</div>
            <div styleName="statChart">
              <ResponsiveContainer height="100%" width="100%">
                <AreaChart data={historical ?? []}>
                  <defs>
                    <linearGradient
                      id="colorEnergy"
                      x1="0"
                      x2="0"
                      y1="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#02A552" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#02A552" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Tooltip
                    content={<ChartTooltip color="#02A552" dataKey="ENERGY" />}
                  />
                  <Area
                    dataKey="ENERGY"
                    fill="url(#colorEnergy)"
                    fillOpacity={1}
                    stroke="#02A552"
                    strokeWidth={2}
                    type="monotone"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div styleName="statCol">
            <div styleName="statHeader">
              <IconIntentionally styleName="statIcon" />
              <div styleName="statTitle">Intention</div>
            </div>
            <div styleName="statScore">
              <span styleName="current">{intentionScore}</span>
              <span styleName="total">/100</span>
            </div>
            <div styleName="statSubtitle">Intention</div>
            <div styleName="statChart">
              <ResponsiveContainer height="100%" width="100%">
                <AreaChart data={historical ?? []}>
                  <defs>
                    <linearGradient
                      id="colorIntentionally"
                      x1="0"
                      x2="0"
                      y1="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#697ecd" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#697ecd" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Tooltip
                    content={<ChartTooltip color="#697ecd" dataKey="INTENTION" />}
                  />
                  <Area
                    dataKey="INTENTION"
                    fill="url(#colorIntentionally)"
                    fillOpacity={1}
                    stroke="#697ecd"
                    strokeWidth={2}
                    type="monotone"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div styleName="historyContainer">
          <div styleName="historyHeader">
            <h3>Experience History</h3>
            <Link styleName="viewAll" to="/past-sessions">
              <span>View all history</span>
              <IconArrowRight styleName="viewAllIcon" />
            </Link>
          </div>
          <div styleName="historySection">
            <div styleName="historyTable">
              <table>
                <tbody>
                  {historyItems.map((item) => {
                    const date = time(item.finishedAt).format('MMM D');
                    const styleClass = item.scoreWord === 'intention'
                      ? 'intentionally'
                      : item.scoreWord;

                    return (
                      <tr key={item.key}>
                        <td styleName="historyIconCell">
                          <div aria-label={item.title} styleName="historyIcon">
                            <item.icon styleName="historyIconSvg" />
                          </div>
                        </td>
                        <td styleName="historyDateCell">{date}</td>
                        <td styleName="historyInfoCell">
                          <div styleName="historyDateMobile">{date}</div>
                          <div styleName="historyTitle">{item.title}</div>
                          <div styleName="historyDescMobile">
                            {item.description}
                          </div>
                        </td>
                        <td styleName="historyDesc">{item.description}</td>
                        <td styleName="historyScoreCell">
                          <span styleName={styleClass}>
                            {item.scoreLabel}
                            {' '}
                            {item.scoreWord}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div styleName="growthContainer">
          <h3 styleName="growthHeader">Growth Summary</h3>
          <div styleName="growthGrid">
            {growthData.map((item) => (
              <div key={item.title} styleName="growthCol">
                <div styleName="growthIcon">
                  <item.icon />
                </div>
                <div styleName="growthValue">{item.value}</div>
                <div styleName="growthTitle">{item.title}</div>
                <div styleName="growthSubtitle">{item.subtitle}</div>
              </div>
            ))}
          </div>
        </div>

        {children}
      </div>
    </div>
  );
};

export default LeftCol;
